import { User, DollarSign, FileText, Car } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { useLanguage } from '../../contexts/LanguageContext';
import { getEmploymentStatusDisplay, getCreditScoreDisplay, formatAddress, formatCurrency } from '../../utils/buyingFormHelpers';
import type { BuyingFormData } from '../../types/buyingForm';
import type { Car as CarType } from "../Listings";

interface ReviewSectionProps {
  formData: BuyingFormData;
  car: CarType;
}

export function ReviewSection({ formData, car }: ReviewSectionProps) {
  const { t } = useLanguage();
  
  return (
    <div className="space-y-6">
      {/* Selected Vehicle Summary */}
      <Card className="border-[#f91bdc]/20">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Car className="text-[#f91bdc]" size={20} />
            <span>{t('buyingForm.selectedVehicle')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.vehicle')}</p>
              <p className="font-semibold">{car.year} {car.make} {car.model}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.dealer')}</p>
              <p className="font-semibold">{car.dealer}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('car.monthlyPayment')}</p>
              <p className="font-semibold text-[#f91bdc]">${car.monthlyPayment}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.term')}</p>
              <p className="font-semibold">{car.termMonths} {t('car.months')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information Review */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="text-[#f91bdc]" size={20} />
            <span>{t('buyingForm.personalInformation')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.fullName')}</p>
              <p className="font-semibold">{formData.firstName} {formData.lastName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.email')}</p>
              <p className="font-semibold">{formData.email || t('buyingForm.notProvided')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.phone')}</p>
              <p className="font-semibold">{formData.phone || t('buyingForm.notProvided')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.dateOfBirth')}</p>
              <p className="font-semibold">{formData.dateOfBirth || t('buyingForm.notProvided')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.streetAddress')}</p>
              <p className="font-semibold">{formatAddress(formData)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Information Review */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <DollarSign className="text-[#f91bdc]" size={20} />
            <span>{t('buyingForm.financialInformation')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.employmentStatus')}</p>
              <p className="font-semibold">{getEmploymentStatusDisplay(formData.employmentStatus, t) || t('buyingForm.notProvided')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.monthlyIncome')}</p>
              <p className="font-semibold">{formatCurrency(formData.monthlyIncome, t)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.employer')}</p>
              <p className="font-semibold">{formData.employer || t('buyingForm.notProvided')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.jobTitle')}</p>
              <p className="font-semibold">{formData.jobTitle || t('buyingForm.notProvided')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.creditScoreRange')}</p>
              <p className="font-semibold">{getCreditScoreDisplay(formData.creditScore, t) || t('buyingForm.notProvided')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('buyingForm.downPaymentLabel')}</p>
              <p className="font-semibold">{formatCurrency(formData.downPayment, t)}</p>
            </div>
          </div>
          {formData.existingDebts && (
            <div className="mt-4">
              <p className="text-sm text-gray-600">{t('buyingForm.existingDebtsLabel')}</p>
              <p className="font-semibold">{formData.existingDebts}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="text-[#f91bdc]" size={20} />
            <span>{t('buyingForm.documentsLabel')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span>{t('buyingForm.photoId')}</span>
              <Badge variant="outline" className="text-green-600 border-green-600">{t('buyingForm.uploaded')}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('buyingForm.proofOfIncome')}</span>
              <Badge variant="outline" className="text-green-600 border-green-600">{t('buyingForm.uploaded')}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('buyingForm.bankStatements')}</span>
              <Badge variant="outline" className="text-green-600 border-green-600">{t('buyingForm.uploaded')}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('buyingForm.proofOfResidence')}</span>
              <Badge variant="outline" className="text-gray-600 border-gray-600">{t('buyingForm.optional')}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('buyingForm.references')}</span>
              <Badge variant="outline" className="text-gray-600 border-gray-600">{t('buyingForm.optional')}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}